import type { ReactNode } from "react";
import { usePlotArea, useXAxisScale, useYAxisScale } from "recharts";
import { layoutEndLabels } from "../../lib/end-label-layout";

export interface CompetitorChartEndLabel {
  id: string;
  value: number;
  valueLabel: string;
  name: string;
  color: string;
  logo: ReactNode;
}

interface CompetitorChartEndLabelsProps {
  labels: CompetitorChartEndLabel[];
  /** X-axis category of the last point, where each line ends. */
  lastCategory: string;
  /** Chart margin to the right of the plot that holds the chips. */
  areaWidth: number;
}

const CHIP_HEIGHT = 26;
const DOT_RADIUS = 3;
/** Chips start just past the end dot so the connector reads as leaving it. */
const CHIP_OFFSET_X = DOT_RADIUS + 1;

/**
 * Rendered inside the chart: a hollow dot on each line's last point, joined to a chip
 * with the brand mark, name and current value in the right margin.
 */
export function CompetitorChartEndLabels({ labels, lastCategory, areaWidth }: CompetitorChartEndLabelsProps) {
  const plot = usePlotArea();
  const xScale = useXAxisScale();
  const yScale = useYAxisScale();
  if (!plot || !yScale) return null;

  const plotRight = plot.x + plot.width;
  const dotX = xScale?.(lastCategory, { position: "middle" }) ?? plotRight;
  const anchors = labels.flatMap((label) => {
    const y = yScale(label.value);
    return y === undefined ? [] : [{ id: label.id, y }];
  });
  const chips = layoutEndLabels(anchors, {
    top: plot.y + CHIP_HEIGHT / 2,
    bottom: plot.y + plot.height - CHIP_HEIGHT / 2,
  });
  const labelById = new Map(labels.map((label) => [label.id, label]));

  return (
    <g data-competitor-chart-end-labels>
      {anchors.map(({ id, y }) => (
        <circle
          key={`dot-${id}`}
          cx={dotX}
          cy={y}
          r={DOT_RADIUS}
          fill="var(--card)"
          stroke={labelById.get(id)?.color}
          strokeWidth={2}
        />
      ))}
      {chips.map(({ id, y }) => {
        const label = labelById.get(id);
        if (!label) return null;
        return (
          <foreignObject
            key={`chip-${id}`}
            x={plotRight + CHIP_OFFSET_X}
            y={y - CHIP_HEIGHT / 2}
            width={areaWidth - CHIP_OFFSET_X}
            height={CHIP_HEIGHT}
          >
            <div className="flex h-full items-center">
              <span className="h-0 w-1.5 shrink-0 border-t-[1.5px]" style={{ borderColor: label.color }} />
              <span
                className="flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-md border border-l-[3px] bg-card pl-1 pr-2 text-xs tabular-nums shadow-sm"
                style={{ borderLeftColor: label.color }}
              >
                {label.logo}
                <span className="max-w-[76px] truncate text-muted-foreground">{label.name}</span>
                <span className="font-semibold text-foreground">{label.valueLabel}</span>
              </span>
            </div>
          </foreignObject>
        );
      })}
    </g>
  );
}
